import {
  type CohortDistributionResponse,
  getAnswerDistribution,
  getCohortDistribution,
  getQuestionAnalytics,
  getSubjectPerformance,
  getTimeDistribution,
  getTopicPerformance,
} from "@/api/analytics"
import type {
  QuestionAnalyticsResponse,
  QuizAnswerDistributionResponse,
  SubjectPerformanceResponse,
  TimeDistributionResponse,
  TopicPerformanceResponse,
} from "@/api/analytics"
import { QuizzesService } from "@/client"
import {
  PerformanceRanking,
  QuestionAnalyticsTable,
  TimeDistributionBar,
} from "@/components/Analytics"
import {
  ClearAttemptsControls,
  DeleteAttemptButton,
  ReopenAttemptButton,
} from "@/components/Analytics/ClearAttemptsControls"
import { ResultsPagination } from "@/components/Analytics/ResultsPagination"
import {
  type SortColumn,
  type SortDir,
  SortableHeader,
} from "@/components/Analytics/SortableHeader"
import { DistributionChart } from "@/components/Charts/DistributionChart"
import { PageShell, PageSkeleton } from "@/components/Layout"
import AnswerDistributionChart from "@/components/Questions/QuestionAnalytics/AnswerDistributionChart"
import { toaster } from "@/components/ui/toaster"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { buildCsvContent, downloadCsv } from "@/utils/csv"
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  GridItem,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Stat,
  Tabs,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { type FormEvent, useState } from "react"
import { LuCalculator, LuDownload, LuRefreshCw } from "react-icons/lu"

export const Route = createFileRoute(
  "/_layout/quiz-management/$quizId/analytics",
)({
  component: QuizAnalytics,
})

interface QuizAnalyticsResponse {
  completion_rate: number | null
  avg_score: number | null
}

interface QuizResultRow {
  attempt_id: string
  user_identifier: string
  student_name: string | null
  school: string | null
  score: number | null
  percentile: number | null
  first_attempt_percentile?: number | null
  completed_at: string
}

interface QuizResultsResponse {
  data: QuizResultRow[]
  count: number
  allow_retake?: boolean
}

const STUDENT_IDENTIFIER_DISPLAY_MAX_CHARS = 20

function formatStudentIdentifierForDashboard(identifier: string): string {
  if (identifier.length <= STUDENT_IDENTIFIER_DISPLAY_MAX_CHARS) {
    return identifier
  }
  return `${identifier.slice(0, STUDENT_IDENTIFIER_DISPLAY_MAX_CHARS)}…`
}

function formatPercentileRank(value: number | null | undefined): string {
  return value !== null && value !== undefined ? value.toFixed(2) : "N/A"
}

function QuizAnalytics() {
  const { quizId } = Route.useParams()
  const [activeTab, setActiveTab] = useState("overview")
  const [resultsSearchInput, setResultsSearchInput] = useState("")
  const [resultsSearchSubmitted, setResultsSearchSubmitted] = useState("")
  const RESULTS_PAGE_SIZE = 50
  const [resultsSortBy, setResultsSortBy] = useState<SortColumn>("score")
  const [resultsSortDir, setResultsSortDir] = useState<SortDir>("desc")
  const [resultsPage, setResultsPage] = useState(0)

  const handleSort = (column: SortColumn) => {
    if (column === resultsSortBy) {
      setResultsSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setResultsSortBy(column)
      setResultsSortDir(column === "student_name" ? "asc" : "desc")
    }
    setResultsPage(0)
  }
  const {
    data: analytics,
    isLoading: isLoadingAnalytics,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useQuery<QuizAnalyticsResponse>({
    queryKey: ["quiz-analytics", quizId],
    queryFn: () =>
      QuizzesService.getQuizAnalytics({
        quizId,
      }) as Promise<QuizAnalyticsResponse>,
    retry: false,
  })

  const {
    data: results,
    isFetching: isFetchingResults,
    refetch: refetchResults,
  } = useQuery<QuizResultsResponse>({
    queryKey: [
      "quiz-results",
      quizId,
      resultsSearchSubmitted,
      resultsSortBy,
      resultsSortDir,
      resultsPage,
    ],
    queryFn: () =>
      QuizzesService.getQuizResults({
        quizId,
        studentIdentifier: resultsSearchSubmitted || undefined,
        sortBy: resultsSortBy,
        sortDir: resultsSortDir,
        skip: resultsPage * RESULTS_PAGE_SIZE,
        limit: RESULTS_PAGE_SIZE,
      }) as Promise<QuizResultsResponse>,
    retry: false,
  })

  const {
    data: cohortDistribution,
    isLoading: isLoadingCohortDistribution,
    refetch: refetchCohortDistribution,
  } = useQuery<CohortDistributionResponse>({
    queryKey: ["quiz-cohort-distribution", quizId],
    queryFn: () => getCohortDistribution(quizId),
    retry: false,
  })

  const {
    data: questionAnalytics,
    isLoading: isLoadingQuestionAnalytics,
    refetch: refetchQuestionAnalytics,
  } = useQuery<QuestionAnalyticsResponse>({
    queryKey: ["quiz-question-analytics", quizId],
    queryFn: () => getQuestionAnalytics(quizId),
    retry: false,
  })

  const {
    data: savedQuestionAnalytics,
    refetch: refetchSavedQuestionAnalytics,
  } = useQuery({
    queryKey: ["quiz-saved-question-analytics", quizId],
    queryFn: () => QuizzesService.getQuizSavedQuestionAnalytics({ quizId }),
    retry: false,
  })

  const questionAnalyticsWithSavedCounts = questionAnalytics
    ? {
        ...questionAnalytics,
        questions: questionAnalytics.questions.map((question) => {
          const saved = savedQuestionAnalytics?.data.find(
            (item) => item.question_id === question.question_id,
          )
          return {
            ...question,
            bookmark_count: saved?.bookmark_count ?? 0,
            note_count: saved?.note_count ?? 0,
          }
        }),
      }
    : undefined

  const {
    data: subjectPerformance,
    isLoading: isLoadingSubjectPerformance,
    refetch: refetchSubjectPerformance,
  } = useQuery<SubjectPerformanceResponse>({
    queryKey: ["quiz-subject-performance", quizId],
    queryFn: () => getSubjectPerformance(quizId),
    retry: false,
  })

  const {
    data: topicPerformance,
    isLoading: isLoadingTopicPerformance,
    refetch: refetchTopicPerformance,
  } = useQuery<TopicPerformanceResponse>({
    queryKey: ["quiz-topic-performance", quizId],
    queryFn: () => getTopicPerformance(quizId),
    retry: false,
  })

  const {
    data: timeDistribution,
    isLoading: isLoadingTimeDistribution,
    refetch: refetchTimeDistribution,
  } = useQuery<TimeDistributionResponse>({
    queryKey: ["quiz-time-distribution", quizId],
    queryFn: () => getTimeDistribution(quizId),
    retry: false,
  })

  const {
    data: answerDistribution,
    isLoading: isLoadingAnswerDistribution,
    refetch: refetchAnswerDistribution,
  } = useQuery<QuizAnswerDistributionResponse>({
    queryKey: ["quiz-answer-distribution", quizId],
    queryFn: () => getAnswerDistribution(quizId),
    retry: false,
  })

  const handleRefresh = async () => {
    await Promise.all([
      refetchAnalytics(),
      refetchResults(),
      refetchCohortDistribution(),
      refetchQuestionAnalytics(),
      refetchSavedQuestionAnalytics(),
      refetchSubjectPerformance(),
      refetchTopicPerformance(),
      refetchTimeDistribution(),
      refetchAnswerDistribution(),
    ])
  }

  const handleExport = async () => {
    try {
      const response = await QuizzesService.exportQuizResults({
        quizId,
        sortBy: resultsSortBy,
        sortDir: resultsSortDir,
      })
      const blob = new Blob([response as unknown as string], {
        type: "text/csv",
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `quiz_${quizId}_results.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Export failed:", error)
      toaster.create({ title: "Export failed", type: "error" })
    }
  }

  const breadcrumbs = useRouteBreadcrumbs("quiz-management.index", [
    { label: "Edit Quiz", href: `/quiz-management/${quizId}/edit` },
    { label: "Analytics", isCurrent: true },
  ])

  const recomputeMutation = useMutation({
    mutationFn: () =>
      QuizzesService.recomputeQuizPercentiles({
        quizId,
      }),
    onSuccess: (data) => {
      toaster.create({
        title: "Percentiles Recomputed",
        description: data.message,
        type: "success",
        duration: 5000,
      })
      refetchAnalytics()
      refetchResults()
      refetchCohortDistribution()
      refetchQuestionAnalytics()
      refetchSubjectPerformance()
      refetchTopicPerformance()
      refetchTimeDistribution()
      refetchAnswerDistribution()
    },
    onError: (error) => {
      toaster.create({
        title: "Recompute Failed",
        description: error.message || "Failed to recompute percentiles",
        type: "error",
        duration: 5000,
      })
    },
  })

  const handleExportDetailedAnalytics = () => {
    if (
      !questionAnalytics ||
      !subjectPerformance ||
      !topicPerformance ||
      !timeDistribution ||
      !answerDistribution
    ) {
      toaster.create({
        title: "Missing Data",
        description:
          "Analytics data is still loading. Please try again shortly.",
        type: "info",
        duration: 4000,
      })
      return
    }

    const sections: string[] = []

    sections.push(
      "Question Analytics",
      buildCsvContent({
        headers: [
          "Question",
          "Correctness %",
          "Avg Time (s)",
          "Subjects",
          "Topics",
        ],
        rows: questionAnalytics.questions.map((row) => [
          row.question_text.replace(/\n/g, " "),
          row.correctness_rate.toFixed(2),
          row.avg_time_seconds.toFixed(2),
          row.subject_names.join("; "),
          row.topic_names.join("; "),
        ]),
      }),
      "",
    )

    sections.push(
      "Subject Performance",
      buildCsvContent({
        headers: ["Subject", "Questions", "Correctness %", "Avg Time (s)"],
        rows: subjectPerformance.subjects.map((row) => [
          row.subject_name,
          row.total_questions,
          row.correctness_rate.toFixed(2),
          row.avg_time_seconds.toFixed(2),
        ]),
      }),
      "",
    )

    sections.push(
      "Topic Performance",
      buildCsvContent({
        headers: ["Topic", "Questions", "Correctness %", "Avg Time (s)"],
        rows: topicPerformance.topics.map((row) => [
          row.topic_name,
          row.total_questions,
          row.correctness_rate.toFixed(2),
          row.avg_time_seconds.toFixed(2),
        ]),
      }),
      "",
    )

    sections.push(
      "Time Distribution",
      buildCsvContent({
        headers: ["Range", "Attempts"],
        rows: timeDistribution.buckets.map((bucket) => [
          bucket.range_label,
          bucket.count,
        ]),
      }),
      "",
    )

    for (const question of answerDistribution.questions) {
      sections.push(
        `Answer Distribution - ${question.question_text.replace(/\n/g, " ")}`,
        buildCsvContent({
          headers: ["Option", "Text", "Correct", "Selection %"],
          rows: question.answer_choices.map((choice) => [
            String.fromCharCode(65 + choice.option_index),
            choice.option_text.replace(/\n/g, " "),
            choice.is_correct ? "Yes" : "No",
            choice.selection_percentage.toFixed(1),
          ]),
        }),
        "",
      )
    }

    downloadCsv(`quiz_${quizId}_detailed_analytics.csv`, sections.join("\n"))
  }

  const actionButtons = (
    <Stack direction={{ base: "column", sm: "row" }} gap={3}>
      <Button
        onClick={handleRefresh}
        variant="outline"
        loading={isLoadingAnalytics || isFetchingResults}
      >
        <LuRefreshCw />
        Refresh Data
      </Button>
      <Button
        onClick={() => recomputeMutation.mutate()}
        colorScheme="blue"
        loading={recomputeMutation.isPending}
      >
        <LuCalculator />
        Recompute Percentiles
      </Button>
      <Button
        onClick={handleExportDetailedAnalytics}
        variant="solid"
        colorScheme="teal"
      >
        <LuDownload />
        Export Detailed CSV
      </Button>
    </Stack>
  )

  const isPageLoading =
    isLoadingAnalytics ||
    isLoadingCohortDistribution ||
    isLoadingQuestionAnalytics ||
    isLoadingSubjectPerformance ||
    isLoadingTopicPerformance ||
    isLoadingTimeDistribution ||
    isLoadingAnswerDistribution

  if (isPageLoading) {
    return (
      <PageShell
        title="Quiz Analytics"
        description="Analyze quiz performance across attempts."
        breadcrumbs={breadcrumbs}
        actions={actionButtons}
      >
        <PageSkeleton variant="stats" rows={4} />
      </PageShell>
    )
  }

  if (analyticsError) {
    return (
      <PageShell
        title="Quiz Analytics"
        description="Analyze quiz performance across attempts."
        breadcrumbs={breadcrumbs}
        actions={actionButtons}
      >
        <Alert.Root status="error">
          <Alert.Title>Failed to load analytics</Alert.Title>
          <Alert.Description>
            {analyticsError instanceof Error
              ? analyticsError.message
              : "Please try again later."}
          </Alert.Description>
        </Alert.Root>
      </PageShell>
    )
  }

  if (!analytics) {
    return null
  }

  const totalItems = cohortDistribution?.overall?.max_score ?? null
  const averageScoreDisplay =
    analytics.avg_score === null
      ? "N/A"
      : totalItems && totalItems > 0
        ? `${analytics.avg_score.toFixed(1)}/${totalItems} (~${Math.round(
            (analytics.avg_score / totalItems) * 100,
          )}%)`
        : analytics.avg_score.toFixed(1)

  return (
    <PageShell
      title="Quiz Analytics"
      description="Analyze quiz performance across attempts."
      breadcrumbs={breadcrumbs}
      actions={actionButtons}
    >
      <Tabs.Root
        value={activeTab}
        onValueChange={({ value }) => setActiveTab(value ?? "overview")}
        variant="subtle"
      >
        <Tabs.List>
          <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
          <Tabs.Trigger value="questions">Questions</Tabs.Trigger>
          <Tabs.Trigger value="subjects">Subjects &amp; Topics</Tabs.Trigger>
          <Tabs.Trigger value="results">Results</Tabs.Trigger>
        </Tabs.List>

        {/* ── Overview Tab ───────────────────────────────────────── */}
        <Tabs.Content value="overview">
          <Stack gap={6} pt={4}>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={6}
            >
              <GridItem>
                <Box p={6} borderWidth="1px" borderRadius="lg">
                  <Stat.Root>
                    <Stat.Label>Completion Rate</Stat.Label>
                    <Stat.ValueText>
                      {analytics.completion_rate !== null
                        ? `${analytics.completion_rate.toFixed(1)}%`
                        : "N/A"}
                    </Stat.ValueText>
                  </Stat.Root>
                </Box>
              </GridItem>
              <GridItem>
                <Box p={6} borderWidth="1px" borderRadius="lg">
                  <Stat.Root>
                    <Stat.Label>Average Score</Stat.Label>
                    <Stat.ValueText>{averageScoreDisplay}</Stat.ValueText>
                  </Stat.Root>
                </Box>
              </GridItem>
            </Grid>

            {cohortDistribution?.is_ready && cohortDistribution.overall ? (
              <Card.Root p={6} variant="outline">
                <Stack gap={3}>
                  <Heading size="sm">Score distribution</Heading>
                  <DistributionChart
                    bins={cohortDistribution.overall.bins}
                    maxScore={cohortDistribution.overall.max_score}
                    height={180}
                  />
                </Stack>
              </Card.Root>
            ) : cohortDistribution && !cohortDistribution.is_ready ? (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>No completed attempts yet</Alert.Title>
                  <Alert.Description>
                    Score distribution will appear once the first attempt is
                    submitted.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            ) : null}

            <TimeDistributionBar
              data={
                timeDistribution ?? {
                  buckets: [],
                  avg_completion_seconds: 0,
                  median_completion_seconds: 0,
                }
              }
            />
          </Stack>
        </Tabs.Content>

        {/* ── Questions Tab ──────────────────────────────────────── */}
        <Tabs.Content value="questions">
          <Stack gap={6} pt={4}>
            <QuestionAnalyticsTable
              title="Question Analytics (sort Bookmarks to find the most saved)"
              data={questionAnalyticsWithSavedCounts?.questions ?? []}
            />

            {answerDistribution && answerDistribution.questions.length > 0 && (
              <Box>
                <Heading size="lg" mb={4}>
                  Answer Distribution by Question
                </Heading>
                <Grid
                  templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }}
                  gap={6}
                >
                  {answerDistribution.questions.map((question, index) => (
                    <AnswerDistributionChart
                      key={question.question_id}
                      data={question.answer_choices}
                      questionText={question.question_text}
                      questionNumber={index + 1}
                    />
                  ))}
                </Grid>
              </Box>
            )}
          </Stack>
        </Tabs.Content>

        {/* ── Subjects & Topics Tab ─────────────────────────────── */}
        <Tabs.Content value="subjects">
          <Stack gap={6} pt={4}>
            <Grid
              templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }}
              gap={6}
            >
              <PerformanceRanking
                title="Subject Performance"
                itemNoun="subject"
                items={(subjectPerformance?.subjects ?? []).map((subject) => ({
                  key: subject.subject_id,
                  name: subject.subject_name,
                  correctnessRate: subject.correctness_rate,
                  avgTimeSeconds: subject.avg_time_seconds,
                  totalQuestions: subject.total_questions,
                }))}
              />
              <PerformanceRanking
                title="Topic Performance"
                itemNoun="topic"
                items={(topicPerformance?.topics ?? []).map((topic) => ({
                  key: topic.topic_id,
                  name: topic.topic_name,
                  correctnessRate: topic.correctness_rate,
                  avgTimeSeconds: topic.avg_time_seconds,
                  totalQuestions: topic.total_questions,
                }))}
              />
            </Grid>

            {cohortDistribution?.is_ready &&
              cohortDistribution.subjects.length > 1 && (
                <Box>
                  <Heading size="md" mb={4}>
                    Score Distribution by Subject
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    {cohortDistribution.subjects.map((subject) => (
                      <Card.Root
                        key={subject.subject_id}
                        p={4}
                        variant="outline"
                      >
                        <Stack gap={2}>
                          <Text fontWeight="semibold" fontSize="sm">
                            {subject.subject_name}
                          </Text>
                          <DistributionChart
                            bins={subject.bins}
                            maxScore={subject.max_score}
                            height={100}
                          />
                        </Stack>
                      </Card.Root>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
          </Stack>
        </Tabs.Content>

        {/* ── Results Tab ───────────────────────────────────────── */}
        <Tabs.Content value="results">
          <Stack gap={4} pt={4}>
            <Stack
              direction="row"
              justify="space-between"
              align="center"
              wrap="wrap"
              gap={2}
            >
              <Heading size="lg">Quiz Results</Heading>
              <Stack direction="row" gap={2} wrap="wrap">
                <ClearAttemptsControls
                  quizId={quizId}
                  totalAttempts={results?.count ?? 0}
                  onDeleted={handleRefresh}
                />
                <Button onClick={handleExport} colorScheme="blue">
                  Export CSV
                </Button>
              </Stack>
            </Stack>
            <Box
              as="form"
              onSubmit={(event: FormEvent<HTMLDivElement>) => {
                event.preventDefault()
                setActiveTab("results")
                setResultsSearchSubmitted(resultsSearchInput.trim())
                setResultsPage(0)
              }}
            >
              <Stack direction={{ base: "column", md: "row" }} gap={2}>
                <Input
                  maxW={{ base: "100%", md: "360px" }}
                  placeholder="Search student identifier"
                  value={resultsSearchInput}
                  onChange={(event) =>
                    setResultsSearchInput(event.target.value)
                  }
                />
                <Button
                  type="submit"
                  colorScheme="blue"
                  loading={isFetchingResults}
                >
                  Search
                </Button>
              </Stack>
            </Box>

            <Box borderWidth="1px" borderRadius="lg" overflowX="auto">
              <Box as="table" width="100%" css={{ borderCollapse: "collapse" }}>
                <Box as="thead" bg="gray.50" _dark={{ bg: "gray.700" }}>
                  <Box as="tr">
                    <Box
                      as="th"
                      textAlign="left"
                      p={4}
                      borderBottomWidth="1px"
                      fontWeight="semibold"
                    >
                      Student Identifier
                    </Box>
                    <SortableHeader
                      label="Student Name"
                      column="student_name"
                      activeSortBy={resultsSortBy}
                      sortDir={resultsSortDir}
                      onSort={handleSort}
                    />
                    <Box
                      as="th"
                      textAlign="left"
                      p={4}
                      borderBottomWidth="1px"
                      fontWeight="semibold"
                    >
                      School
                    </Box>
                    <SortableHeader
                      label="Score"
                      column="score"
                      activeSortBy={resultsSortBy}
                      sortDir={resultsSortDir}
                      onSort={handleSort}
                    />
                    {results?.allow_retake ? (
                      <>
                        <Box
                          as="th"
                          textAlign="left"
                          p={4}
                          borderBottomWidth="1px"
                          fontWeight="semibold"
                          title="Percentile rank of the student's first attempt among every student's first attempt"
                        >
                          1st Attempt PR
                        </Box>
                        <Box
                          as="th"
                          textAlign="left"
                          p={4}
                          borderBottomWidth="1px"
                          fontWeight="semibold"
                          title="Percentile rank of this attempt among all attempts, including retakes"
                        >
                          All Attempts PR
                        </Box>
                      </>
                    ) : (
                      <Box
                        as="th"
                        textAlign="left"
                        p={4}
                        borderBottomWidth="1px"
                        fontWeight="semibold"
                      >
                        Percentile
                      </Box>
                    )}
                    <SortableHeader
                      label="Date Attempted"
                      column="completed_at"
                      activeSortBy={resultsSortBy}
                      sortDir={resultsSortDir}
                      onSort={handleSort}
                    />
                    <Box
                      as="th"
                      textAlign="left"
                      p={4}
                      borderBottomWidth="1px"
                      fontWeight="semibold"
                    >
                      Actions
                    </Box>
                  </Box>
                </Box>
                <Box as="tbody">
                  {results?.data.map((row) => (
                    <Box
                      as="tr"
                      key={row.attempt_id}
                      _hover={{
                        bg: "gray.50",
                        _dark: { bg: "gray.800" },
                      }}
                    >
                      <Box as="td" p={4} borderBottomWidth="1px">
                        {formatStudentIdentifierForDashboard(
                          row.user_identifier,
                        )}
                      </Box>
                      <Box as="td" p={4} borderBottomWidth="1px">
                        {row.student_name || "—"}
                      </Box>
                      <Box as="td" p={4} borderBottomWidth="1px">
                        {row.school || "—"}
                      </Box>
                      <Box as="td" p={4} borderBottomWidth="1px">
                        {row.score !== null ? row.score.toFixed(1) : "N/A"}
                      </Box>
                      {results?.allow_retake && (
                        <Box as="td" p={4} borderBottomWidth="1px">
                          {formatPercentileRank(row.first_attempt_percentile)}
                        </Box>
                      )}
                      <Box as="td" p={4} borderBottomWidth="1px">
                        {formatPercentileRank(row.percentile)}
                      </Box>
                      <Box as="td" p={4} borderBottomWidth="1px">
                        {new Date(row.completed_at).toLocaleString()}
                      </Box>
                      <Box as="td" p={4} borderBottomWidth="1px">
                        <Stack direction="row" gap={1}>
                          <ReopenAttemptButton
                            quizId={quizId}
                            attemptId={row.attempt_id}
                            studentIdentifier={formatStudentIdentifierForDashboard(
                              row.user_identifier,
                            )}
                            onReopened={handleRefresh}
                          />
                          <DeleteAttemptButton
                            quizId={quizId}
                            attemptId={row.attempt_id}
                            studentIdentifier={formatStudentIdentifierForDashboard(
                              row.user_identifier,
                            )}
                            onDeleted={handleRefresh}
                          />
                        </Stack>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              {results?.data.length === 0 && (
                <Box p={8} textAlign="center" color="gray.500">
                  No results available yet
                </Box>
              )}
            </Box>
            <ResultsPagination
              page={resultsPage}
              pageSize={RESULTS_PAGE_SIZE}
              count={results?.count ?? 0}
              onPageChange={setResultsPage}
            />
          </Stack>
        </Tabs.Content>
      </Tabs.Root>
    </PageShell>
  )
}
